import Vue from 'vue';
import {
  addGroupingInResponseData,
  LinkCellRenderer,
  autoResize,
  getPinnedRowData
} from '@/helpers/renderHelpers';
import {
  addColIdToColumns,
  handlerResponse,
  SumRows
} from '@/helpers/commonHelpers';
import gridProps from '@/gridProps';

export default {
  namespaced: true,
  state: {
    contracts: [],
    contractsList: [],
    relatedContracts: [],
    columns: [],
    documents: [],
    pinnedRowData: [],
    comments: [],
    bindPackages: [],
    unbindPackages: [],
    unbindPackagesOrdersCount: 0,
    bindPackagesColumns: [
      {field: "movement_name", headerName: "Acceptance name", cellRenderer: "MovementLink"},
      {
        headerName: "Delivered", children: [
        {field: "delivered_nominal", headerName: "nom"},
        {field: "delivered_stock", headerName: "stock"}]
      },
      {field: "ordered", headerName: "Ordered"},
      {field: "measure_type", headerName: "Measure type"}
    ],
    unbindPackagesColumns: [
      {field: "package_nr", headerName: "Package №"},
      {field: "package_name", headerName: "Package name"},
      {field: "delivered_stock", headerName: "Delivered stock"},
      {field: "measure_type", headerName: "Measure type"}
    ],
    contractsCount: null,
    contractTypes: ['direct', 'exchange'],
    contractStatuses: ['active', 'completed'],
    gridOptions: {
      rowGroupPanelShow: 'always',
      animateRows: true,
      rowSelection: 'multiple',
      aggFuncs: {
        'SumRows': SumRows
      },
      components: {
        'ContractLink': LinkCellRenderer('id', '/contracts/'),
        'BuyerLink': LinkCellRenderer('buyer_id', '/partners/'),
        'SellerLink': LinkCellRenderer('seller_id', '/partners/'),
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onRowDataChanged() {
        if (this.rowData && this.rowData.length) {
          autoResize(this);
        } else {
          this.api && this.api.hideOverlay();
          this.api && this.api.showNoRowsOverlay();
        }
      }
    },
    gridName: ''
  },
  getters: {
    gridOptions(state) {
      return {...state.gridOptions, ...gridProps};
    },
    bindPckgsGridOptions(state) {
      return {
        treeData: true,
        animateRows: true,
        groupDefaultExpanded: -1,
        components: {
          'MovementLink': LinkCellRenderer('movement_id', '/operation/'),
        },
        getDataPath: function (data) {
          return data.order_nr;
        },
        autoGroupColumnDef: {
          headerName: "Purchase Order",
          cellRendererParams: {
            suppressCount: true
          },
        }
      };
    },
    unbindPckgsGridOptions(state) {
      return {
        treeData: true,
        animateRows: true,
        groupDefaultExpanded: -1,
        getDataPath: function (data) {
          return data.movement_name;
        },
        autoGroupColumnDef: {
          headerName: "Operation name",
          cellRendererParams: {
            suppressCount: true
          }
        }
      };
    },
    contractsList: state => state.contractsList,
    relatedContracts: state => state.relatedContracts
  },
  mutations: {
    setRelatedContracts: (state, data) => Vue.set(state,'relatedContracts', data),
    setData(state, data) {
      if (data.data) {
        state.contracts = addGroupingInResponseData(data.data.results);
        state.columns = addColIdToColumns(data.used_columns);
        state.contractsCount = data.data.count;
        state.pinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.contracts = data;
        state.pinnedRowData = data;
      }
      if (data.grid_name) {
        state.gridName = data.grid_name;
      }
    },
    setContractsList(state, data) {
      state.contractsList = data;
    },
    setDistibutedPackages(state, data) {
      state.bindPackages = data;
    },
    setUndistibutedPackages(state, data) {
      state.unbindPackagesOrdersCount = data.length;
      state.unbindPackages = data;
    },
    setCommentsList(state, data) {
      state.comments = data;
    },
    setDocumentsList(state, data) {
      state.documents = data;
    },
    setTotalData(state, data) {
      if (data.data) {
        state.pinnedRowData = getPinnedRowData(data.total, 'name', 'value');
      } else {
        state.pinnedRowData = data;
      }
    }
  },
  actions: {
    clearRelatedContracts: (store) => store.commit('setRelatedContracts', []),
    updateContractsTotal(store, payLoad) {
      if (store.getters.gridOptions.api) {
        store.getters.gridOptions.api.showLoadingOverlay();
      }
      Vue.http.get('purchase/search_for_contracts/', {params: payLoad})
        .then(response => response.json())
        .then(data => {
          store.commit('setTotalData', data);
        });
    },
    loadContracts(store, payLoad) {
      if (store.getters.gridOptions.api) {
        store.getters.gridOptions.api.showLoadingOverlay();
      }
      Vue.http.get('purchase/search_for_contracts/', {params: payLoad})
        .then(response => response.json())
        .then(data => {
          store.commit('setData', data);
        });
    },
    async loadRelatedToOperationContracts(store, payLoad) {
      await Vue.http.get('purchase/contracts/related_to_operation_ids/', {params: payLoad})
        .then(response => response.json())
        .then(data => {
          store.commit('setRelatedContracts', data)
        });
    },
    async loadRelatedToOrderContracts(store, payLoad) {
      await Vue.http.get('purchase/contracts/related_to_order_ids/', {params: payLoad})
        .then(response => response.json())
        .then(data => {
          store.commit('setRelatedContracts', data)
        });
    },
    loadContractsList(store, payLoad) {
      Vue.http.get('purchase/contracts/ids/', {params: payLoad})
        .then(response => response.json())
        .then(data => {
          store.commit('setContractsList', data);
        });
    },
    loadDistributedPackages(store, payLoad) {
      Vue.http.get(`purchase/contracts/${payLoad}/distribution/per_order/`)
        .then(response => response.json())
        .then(data => {
          store.commit('setDistibutedPackages', data);
        });
    },
    loadUndistributedPackages(store, payLoad) {
      Vue.http.get(`purchase/contracts/${payLoad}/distribution/per_operation/`)
        .then(response => response.json())
        .then(data => {
          store.commit('setUndistibutedPackages', data);
        });
    },
    loadBindedPackages(store, payLoad) {
      Vue.http.get(`purchase/contracts/${payLoad}/distribution/per_order/`)
        .then(response => response.json())
        .then(data => {
          store.commit('setDistibutedPackages', data);
        });
    },
    loadCommentsList(store, payLoad) {
      Vue.http.get(`purchase/contracts/${payLoad.id}/comments/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setCommentsList', data);
        });
    },
    loadDocumentsList(store, payLoad) {
      Vue.http.get(`purchase/contracts/${payLoad.id}/attachments/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setDocumentsList', data);
        });
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/purchases/contracts/purchaseContracts.js